import { lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, type Location } from 'react-router-dom';
import { ActivityIndicator } from './components/ActivityIndicator';
import { CursorFollower } from './components/CursorFollower';
import { SignInSheet } from './components/SignInSheet';
import DiscoveryPage from './pages/DiscoveryPage';
import { track, trackPage } from './lib/analytics';
import { onSessionExpired } from './lib/auth';
import { recheckImpressions } from './lib/impressions';
import { startSmoothScroll } from './lib/motion';
import { strings } from './lib/strings';

const UniverseViewer = lazy(() => import('./viewer/UniverseViewer'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const CallbackPage = lazy(() => import('./pages/CallbackPage'));
const StudioPage = lazy(() => import('./studio/StudioPage'));

function backgroundOf(location: Location): Location | null {
  const state = location.state as { background?: Location } | null;
  return state?.background ?? null;
}

function PageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-muted">
      <ActivityIndicator size="lg" label={strings.loading} />
    </div>
  );
}

function SheetFallback() {
  return (
    <div className="fixed inset-0 z-overlay flex items-center justify-center bg-scrim text-white">
      <ActivityIndicator size="lg" label={strings.loading} />
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const background = backgroundOf(location);
  const wallRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    wallRef.current?.toggleAttribute('inert', Boolean(background));
    recheckImpressions();
  }, [background]);

  useEffect(() => {
    trackPage();
  }, [location.pathname, location.search]);

  useEffect(() => onSessionExpired(() => track('session_expired', {})), []);

  useEffect(() => startSmoothScroll(), []);

  return (
    <>
      <div ref={wallRef} className="wall bg-canvas">
        <Suspense fallback={<PageFallback />}>
          <Routes location={background ?? location}>
            <Route path="/" element={<DiscoveryPage />} />
            <Route path="/search" element={<DiscoveryPage />} />
            <Route path="/c/:slug" element={<CollectionPage />} />
            <Route path="/view/:id" element={<UniverseViewer overlay={false} />} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route path="/studio" element={<StudioPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {background && (
        <Suspense fallback={<SheetFallback />}>
          <Routes>
            <Route path="/view/:id" element={<UniverseViewer overlay />} />
          </Routes>
        </Suspense>
      )}
      <SignInSheet />
      <CursorFollower />
    </>
  );
}
